import React, { useState, ReactElement, Children, isValidElement } from 'react'
import { StyledContainer, SlidingBackground, TabButton } from './styles'
import { Box, StackProps, SxProps, Theme, Tooltip } from '@mui/material'

/* Only allow TabButton as children */
type AllowedChild = ReactElement<React.ComponentProps<typeof TabButton>>

type TabButtonChildren = [AllowedChild, AllowedChild] | [AllowedChild, AllowedChild, AllowedChild | false]

export interface TabButtonGroupBaseProps extends StackProps {
  tabIndex?: number
  children: TabButtonChildren
  defaultTabIndex?: number
  disabled?: boolean
  styledContainerSx?: SxProps<Theme>
}

/**
 * TabButtonGroup is a group of TabButton components with sliding background highlight.
 * Only TabButton components are allowed as children.
 */
const TabButtonGroupBase: React.FC<TabButtonGroupBaseProps> = ({
  children,
  tabIndex,
  defaultTabIndex = 0,
  disabled = false,
  styledContainerSx,
  ...stackProps
}) => {
  /* only allow TabButton */
  const childArray = Children.toArray(children).filter((child): child is AllowedChild => isValidElement(child) && child.type === TabButton)

  const maxIndex = Math.max(childArray.length - 1, 0)
  const initialTabIndex = defaultTabIndex > maxIndex ? 0 : defaultTabIndex
  const [selectedTabIndex, setSelectedTabIndex] = useState(initialTabIndex)

  const { sx: stackSx, ...restStackProps } = stackProps
  const mergedStyledContainerSx = [{ width: '100%', p: 1 }, stackSx, styledContainerSx].filter(Boolean) as unknown as SxProps<Theme>

  return (
    <StyledContainer sx={mergedStyledContainerSx} {...restStackProps} disabled={disabled}>
      <Box position="relative" display="flex">
        <SlidingBackground sx={stackSx} selectedindex={tabIndex ?? selectedTabIndex} childcount={childArray.length} disabled={disabled} />

        {childArray.map((child, index) => {
          const isActive = (tabIndex ?? selectedTabIndex) === index
          const { disabled: isNativeDisabled, isDisabled, tooltip, ...otherChildProps } = child.props

          const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
            setSelectedTabIndex(index)
            child.props?.onClick?.(event)
          }

          const isButtonDisabled = disabled || isDisabled || isNativeDisabled

          return (
            <Tooltip key={child.key} title={tooltip}>
              <Box component="span" style={{ flex: 1, display: 'block', width: '100%' }}>
                <TabButton
                  sx={{
                    ...stackSx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                  }}
                  {...otherChildProps}
                  variant="text"
                  isActive={isActive}
                  isDisabled={isDisabled}
                  disabled={isButtonDisabled}
                  onClick={handleOnClick}
                />
              </Box>
            </Tooltip>
          )
        })}
      </Box>
    </StyledContainer>
  )
}

export default TabButtonGroupBase
